import React, { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';
import { Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import type { WaveDistributionTimestep } from 'types/cameraInsights';
import type { Units } from 'types/units';

import classNames from 'classnames';
import useWaveDistributionGraph from './hooks/useWaveDistributionGraph';

import graphStyles from '../../../Graphs.module.scss';
import styles from './WaveDistributionGraph.module.scss';

interface WaveDistributionGraphProps {
  data?: WaveDistributionTimestep[];
  units?: Pick<Units, 'waves'>;
}

const WaveDistributionGraph: React.FC<WaveDistributionGraphProps> = ({ data, units }) => {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { height: graphHeight = 91, width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const { setupGraph } = useWaveDistributionGraph({
    canvasRef,
    data,
    graphHeight,
    graphWidth,
    units,
  });

  const debouncedSetupGraph = useMemo(
    () => debounce(setupGraph, 100, { leading: true, trailing: true }),
    [setupGraph],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetupGraph();

    return () => {
      debouncedSetupGraph.cancel();
    };
  }, [graphWidth, debouncedSetupGraph]);

  return (
    <div className={styles.waveDistributionGraph} data-testid="wave-distribution-graph">
      <dl
        className={classNames(graphStyles.legend, styles.legend)}
        data-testid="wave-distribution-legend"
      >
        <Typography component="dt" variant="caption2" className={styles.legendTotal}>
          Total waves
        </Typography>
        <dd style={visuallyHidden}>Amount of total waves in a given amount of time</dd>
        <Typography component="dt" variant="caption2" className={styles.legendSurfed}>
          Surfed waves
        </Typography>
        <dd style={visuallyHidden}>Amount of waves ridden by surfers in a given amount of time</dd>
      </dl>
      <div className={styles.graph} ref={canvasRef} />
    </div>
  );
};

export default WaveDistributionGraph;
